<template>
  <el-dialog
    :visible.sync="showDialog"
    :width="modalWidth"
    top="15vh"
    :modal-append-to-body="false"
    :before-close="handleClose"
    :custom-class="`${
      addCustomScrollable ? 'custom-scrollable' : 'hide-cancel-button'
    }`"
  >
    <div test-id="main">
      <!-- Enter Transfer Details -->
      <div v-if="isEnterTransferDetails">
        <h2 class="text-root-2 text-lg">Enter Transfer Details</h2>
        <el-form
          :model="settleCustomerTransferForm"
          ref="settleCustomerTransferForm"
          size="small"
        >
          <div class="grid grid-cols-12 form-item bg-root mt-8">
            <div
              class="flex settlement-amount flex-col col-span-8 md:col-span-9 px-5"
            >
              <label class="font-normal text-root-3 text-xs mt-3"
                >Settlement amount</label
              >
              <div class="flex items-center pt-0">
                <span class="text-root-3 pb-4 text-xl md:text-2xl">{{
                  country.symbol
                }}</span>
                <el-form-item prop="settlementAmount">
                  <el-input
                    class="text-lg md:text-xl"
                    name="amount"
                    readonly
                    v-model="amountToBePaid"
                    placeholder="0.00"
                  >
                  </el-input
                ></el-form-item>
              </div>
            </div>
            <div
              class="col-span-4 md:col-span-3 flex items-center justify-center bg-root-1 drop-down"
            >
              <span class="flex items-center">
                <div class="flex gap-x-2 items-center w-full">
                  <currency-icon :currency="country.name" />
                  <span class="sm text-root-5 md:w-12">{{
                    country.currency
                  }}</span>
                </div>
                <el-select
                  v-model="currencyActive"
                  @change="changeActiveCountry"
                  placeholder=""
                  class="select"
                >
                  <el-option
                    class=""
                    v-for="item in determineCurrencyOptions"
                    :key="item.currency"
                    :label="item.currency"
                    :value="item.currency"
                    :disabled="!transferable(item.currency)"
                  >
                    <div class="flex">
                      <span class="pr-2" style="float: left">{{
                        item.currency
                      }}</span>
                      -
                      <span
                        class="pl-2"
                        style="float: right; color: #8492a6; font-size: 13px"
                        >{{ item.currencyFull }}</span
                      >
                    </div>
                  </el-option>
                </el-select>
              </span>
            </div>
          </div>
          <div class="px-6 list">
            <ul class="font-normal text-root-4 text-xs py-5 border-l list-disc">
              <li class="flex items-center justify-between px-4">
                <span class="text-primary font-bold"
                  >1
                  {{
                    !this.isDivisionOperator
                      ? country.currency
                      : secondaryCurrency
                  }}
                  = {{ exchangeRateBase }}
                  {{
                    !this.isDivisionOperator
                      ? secondaryCurrency
                      : country.currency
                  }}</span
                >
                <span>Exchange Rate</span>
              </li>
              <li class="flex items-center justify-between px-4 mt-4">
                <span class="text-primary font-bold">
                  {{ country.currency }}
                  {{ addComma(computeSettlementAmount.toString()) }}
                </span>
                <span>Amount Converted</span>
              </li>
              <li class="flex items-center justify-between px-4 mt-4">
                <span class="text-primary font-bold">
                  {{ parseMoneyAll(fees, country.currency) }}
                </span>
                <span>Our Fees</span>
              </li>
            </ul>
          </div>
          <div class="grid grid-cols-12 form-item bg-root">
            <div class="flex flex-col col-span-8 md:col-span-9 px-5">
              <label class="font-normal text-root-3 text-xs mt-3"
                >Destination amount</label
              >
              <div class="flex items-center pt-0">
                <span class="text-root-3 pb-4 text-xl md:text-2xl">{{
                  `${country2.symbol}`
                }}</span>
                <el-form-item prop="enteredAmount">
                  <el-input
                    class="text-lg md:text-xl"
                    readonly
                    name="amount"
                    placeholder="0.00"
                    v-model="destinationAmount"
                  >
                  </el-input
                ></el-form-item>
              </div>
            </div>
            <div
              class="col-span-4 md:col-span-3 flex items-center justify-center bg-root-1 drop-down"
            >
              <span class="flex gap-x-2 items-center -ml-2">
                <currency-icon :currency="secondaryCountry" />
                <span class="sm text-root-5">{{ secondaryCurrency }}</span>
              </span>
            </div>
          </div>
        </el-form>
      </div>
      <!-- Transaction Review -->
      <div v-if="isTransactionReview">
        <h2 class="text-root-2 text-lg pb-5 border-b flex items-center gap-x-5">
          <span class="cursor-pointer" @click="previousHandler">
            <arrow-left />
          </span>
          <span>Transaction Review</span>
        </h2>
        <ul class="text-root-4 text-sm font-light">
          <li class="flex items-center justify-between my-4">
            <span>Destination Amount:</span>
            <span class="font-semibold text-root-2">{{
              `${country2.symbol}${destinationAmount}`
            }}</span>
          </li>
          <li class="flex items-center justify-between mb-4">
            <span>Our fees:</span>
            <span>{{ `${fees} ${determineSettlementCurrency.currency}` }}</span>
          </li>
          <li class="flex items-center justify-between mb-4">
            <span>Amount Converted:</span>
            <span>
              {{
                `${addComma(computeSettlementAmount.toString())} ${
                  country.currency
                }`
              }}</span
            >
          </li>
          <li class="flex items-center justify-between mb-4">
            <span>Exchange Rate:</span>
            <span>
              1
              {{
                !this.isDivisionOperator ? country.currency : secondaryCurrency
              }}
              = {{ exchangeRateBase }}
              {{
                !this.isDivisionOperator ? secondaryCurrency : country.currency
              }}</span
            >
          </li>
          <li class="flex items-center justify-between mb-4">
            <span>Amount to be Paid:</span>
            <span class="font-semibold text-root-2">{{
              `${addComma(amountToBePaid.toString())} ${country.currency}`
            }}</span>
          </li>
        </ul>
      </div>
      <o-button
        v-if="step < 4 && !showDepositWalletDialog"
        class="bg-primary text-white w-full rounded-3xl mt-8 shadow-sm py-1.5"
        @click="continueHandler"
        :disabled="isLoading"
        :loading="isLoading"
        >{{ isLoading ? 'Initiating...' : 'Continue' }}</o-button
      >
      <!-- Transaction Done -->
      <make-payment
        v-if="isTransactionDone"
        useType="externalApi"
        @closeDialog="closeDialog"
      />
      <!-- dialogs -->
      <deposit-Wallet
        v-if="showDepositWalletDialog"
        :showDialog="showDepositWalletDialog"
        @closeDialog="handleClose"
        @showMakePayment="initiateAssetTransferHandler"
        use="externalApi"
      />
    </div>
  </el-dialog>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import { COUNTRIES } from '@/utils/constants'
import localDeposit from '@/mixins/localDeposit.js'
import country from '@/mixins/getCountryDetails.js'
import { AddComma, ParseMoneyAll, FormatAmount } from '@/utils/helpers'
import tenant from '@/mixins/tenant.js'
export default {
  name: 'SettleCustomerTransferDialog',
  mixins: [localDeposit, country, tenant],
  props: {
    showDialog: {
      type: Boolean,
      default: false
    },
    batchDetails: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    ArrowLeft: () => import('@/assets/icons/ArrowLeft.vue'),
    CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue'),
    MakePayment: () =>
      import('@/components/terminal/terminalSkeleton/MakePayment.vue'),
    DepositWallet: () =>
      import('@/components/terminal/dialogs/DepositWallet.vue')
  },
  data () {
    return {
      currencyOptions: COUNTRIES,
      destinationCurrency: 'NGN',
      addCustomScrollable: true,
      showDepositWalletDialog: false,
      hasSettledTransfer: false,
      amount: '',
      destinationRate: 0,
      settleCustomerTransferForm: {
        settlementAmount: 0,
        destinationAmount: this.destinationAmount
      },
      step: 1,
      isLoading: false
    }
  },
  computed: {
    destinationAmount () {
      return this.$store.state.externalApi.batchDetails.items.amount
        .toString()
        .split('.')[0]
        .replace(/\D/g, '')
        .replace(/^0+/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    modalWidth () {
      if (this.addCustomScrollable) {
        return breakpoints.lgAndUp ? '30%' : breakpoints.mdAndUp ? '70%' : '90%'
      }
      return breakpoints.lgAndUp ? '35%' : breakpoints.mdAndUp ? '70%' : '90%'
    },
    fees () {
      if (this.percentageFees) {
        return (
          (this.percentageFees / 100) *
          this.computeSettlementAmount
        ).toFixed(2)
      } else if (this.flatFees) {
        return parseFloat(this.flatFees)
      } else {
        return 0
      }
    },
    assetDepositId () {
      return this.$store.state.terminal.assetDepositId
    },
    currencyActive: {
      get: function () {
        return this.$store.state.common.activeCurrency
      },
      set: function (newValue) {
        this.$store.dispatch('common/setActiveCurrency', newValue)
      }
    },
    determineSettlementCurrency () {
      return this.currencyOptions.find(
        (currency) => currency.currency === this.activeCurrency
      )
    },
    determineDestinationCurrency () {
      return this.currencyOptions.find(
        (currency) => currency.currency === this.batchDetails.currency
      )
    },
    determineCurrencyOptions () {
      return !this.transferDestinationIsGlobalFiat
        ? this.currencyOptions.filter(
            (country) =>
              country.name === 'usa' ||
              country.name === 'usdt-ethereum' ||
              country.name === 'usdc-ethereum'
          )
        : this.currencyOptions.filter((country) => country.name !== 'usa')
    },
    getAmountFromUSDRate () {
      return (
        FormatAmount(this.destinationAmount) / this.exchangeRateBase
      ).toFixed(2)
    },
    computeSettlementAmount: function () {
      if (this.transferSourceIsGlobalFiat || this.transferSourceIsAsset) {
        return this.getAmountFromUSDRate
      } else {
        return (
          this.exchangeRateBase * FormatAmount(this.destinationAmount)
        ).toFixed(2)
      }
    },
    amountToBePaid () {
      return (+this.computeSettlementAmount + +this.fees).toFixed(2)
    },
    batchDate () {
      return this.$route.params.id
    },
    walletAddresses () {
      return this.$store.state.transactions.walletAddresses.items || []
    },
    isTransactionReview () {
      return (
        (this.step === 2 && !this.showDepositWalletDialog) ||
        (this.step === 3 && !this.showDepositWalletDialog)
      )
    },
    isEnterTransferDetails () {
      return this.step === 1
    },
    isTransactionDone () {
      return this.step === 4
    }
  },
  methods: {
    filterCurrencyOptions () {
      if (!this.tenantIsOval) {
        this.currencyOptions = COUNTRIES.filter((country) => !country.network)
      }
    },
    parseMoneyAll (val, currency) {
      return ParseMoneyAll(val, currency)
    },
    handleClose () {
      this.$emit('closeDialog')
      if (this.hasSettledTransfer) this.$router.push('/transactions')
    },
    closeDialog () {
      this.handleClose()
    },
    previousHandler () {
      this.step--
    },
    changeActiveCountry () {
      if (this.currencyActive !== this.activeCurrency) {
        this.$store.dispatch('common/setActiveCurrency', this.currencyActive)
      }
    },
    addComma (val) {
      return AddComma(val)
    },
    transferable (currency) {
      if (currency === 'USDT' || currency === 'USDC') {
        const walletDetails = this.walletAddresses.find(
          (wallet) => wallet.asset === currency && wallet.reason === 'transfer'
        )
        return walletDetails
      }
      return true
    },
    async initiateAssetTransferHandler () {
      const vm = this
      const payload = {
        assetDepositID: this.assetDepositId,
        batchDate: this.batchDate,
        destinationCurrency: this.determineDestinationCurrency.currency
      }
      await this.$store
        .dispatch('externalApi/settleCustomerAssetTransfer', payload)
        .then(() => {
          this.$store.dispatch(
            'transactions/setCurrentTransaction',
            this.$store.state.externalApi.settledTransferInfo.transfer || {}
          )
          this.$store.dispatch(
            'transactions/setTransactionDetails',
            this.$store.state.externalApi.settledTransferInfo.transfer || {}
          )
          this.hasSettledTransfer = true
          vm.$message({
            showClose: true,
            message: 'Transfer Settled Successfully',
            type: 'success'
          })
          this.handleClose()
        })
        .catch((error) => {
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$message({
            showClose: true,
            message: this.httpErrorMessage,
            type: 'error'
          })
          this.handleClose()
        })
    },
    async settleCustomerTransfer () {
      this.isLoading = true
      const vm = this
      const payload = {
        batchDate: this.batchDate,
        currency: this.determineSettlementCurrency.currency,
        destinationCurrency: this.determineDestinationCurrency.currency
      }
      await this.$store
        .dispatch('externalApi/settleCustomerTransfer', payload)
        .then(() => {
          this.isLoading = false
          this.hasSettledTransfer = true
          this.$store.dispatch(
            'transactions/setCurrentTransaction',
            this.$store.state.externalApi.settledTransferInfo.transfer || {}
          )
          this.$store.dispatch(
            'transactions/setTransactionDetails',
            this.$store.state.externalApi.settledTransferInfo.transfer || {}
          )
          this.step = 4
        })
        .catch((error) => {
          this.isLoading = false
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$message({
            showClose: true,
            message: this.httpErrorMessage,
            type: 'error'
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async continueHandler () {
      if (this.step < 2) {
        this.step++
        const details = {
          transferReason: '',
          debitAmount: +this.amountToBePaid,
          fees: this.fees,
          recipientAmount: FormatAmount(this.destinationAmount),
          partnerCommission: 0
        }
        this.$store.dispatch('terminal/setTransferDetails', details)
      } else if (this.transferSourceIsAsset) {
        this.showDepositWalletDialog = true
      } else {
        await this.settleCustomerTransfer()
      }
    }
  },
  watch: {
    step (newVal) {
      if (newVal === 4) {
        this.addCustomScrollable = false
      }
    }
  },
  beforeMount () {
    if (this.exchangeRate === 0) {
      const message = 'Cannot get current exchange rate at the moment'
      this.$message({
        showClose: true,
        message: `${message}, please try again Later`,
        type: 'error',
        duration: 5000
      })
      this.$store.dispatch('common/setError', message)
      setTimeout(() => {
        this.$router.push('/system-error')
      }, 3000)
    }
  },
  mounted () {
    this.filterCurrencyOptions()
  },
  destroyed () {
    this.$store.dispatch('transactions/setCurrentTransaction', {})
    this.$store.dispatch('transactions/setTransactionDetails', {})
  }
}
</script>

<style scoped>
.grey-bg {
  background: #e0e0e0;
}
>>> .el-button {
  background-color: rgba(242, 103, 34, var(--tw-bg-opacity));
  color: white;
}
>>> .el-input__inner {
  border-radius: 6px;
  height: 45px;
  width: 100%;
}
>>> .el-select {
  margin-right: -2px;
}

>>> .el-select .el-input .el-select__caret {
  color: var(--color-primary);
  font-size: 13px;
  font-weight: 800;
  transition: transform 0.3s;
  transform: rotateZ(180deg);
  cursor: pointer;
}
>>> .form-item {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 1px 3px rgba(3, 2, 7, 0.12);
  border-radius: 15px;
}
.form-item-2 {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 1px 3px rgba(3, 2, 7, 0.12);
  border-radius: 15px;
}
.drop-down {
  border-radius: 0px 15px 15px 0px;
}
>>> .el-popper[x4placement^='bottom'] {
  margin-left: -100px;
}
>>> .el-input__inner {
  border: none;
  outline: none;
  padding-right: 0rem;
  background: transparent;
  font-size: 1.4rem;
  font-weight: 700;
  width: 100%;
}
>>> .el-select .el-input__inner {
  font-size: 0.8rem;
  margin-left: -0.5rem;
}

.sm {
  font-size: 0.8rem;
}

.list ul {
  list-style: none;
  border-left: 1px solid var(--text-3);
}
.list li {
  position: relative;
}
.list li::before {
  content: '•';
  font-size: 1.8rem;
  position: absolute;
  left: -0.4rem;
  top: 0;
  color: var(--color-primary);
}
.list li:last-of-type::before {
  left: -0.5rem;
}
>>> .hide-cancel-button .el-dialog__headerbtn {
  display: none !important;
}

@media screen and (max-width: 767px) {
  >>> .el-input__inner {
    font-size: 1.5rem;
  }

  >>> .el-select {
    margin-right: -10px;
    width: 80px;
  }
  >>> .el-select .el-input .el-select__caret {
    margin-right: 0.15rem;
  }
  >>> .el-select .el-input__inner {
    font-size: 1rem;
  }
}
</style>
